import AppShell from "@/components/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  classKey,
  classLabelFromKey,
  getFeeInstallmentProgress,
  getSessionUser,
  getStudentRecordForSession,
  isTestPaymentStudent,
} from "@/lib/eisStore";
import { initiateFeePayment } from "@/lib/paymentApi";
import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

function DetailRow({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 py-3 border-b border-slate-100 last:border-b-0">
      <p className="text-sm text-slate-500">{label}</p>
      <p className="text-sm font-semibold text-slate-900 text-right">{value}</p>
    </div>
  );
}

export default function Checkout() {
  const navigate = useNavigate();
  const location = useLocation();
  const user = getSessionUser();
  const studentRecord = getStudentRecordForSession(user) || user || {};
  const allowTestPayment = isTestPaymentStudent(user) || isTestPaymentStudent(studentRecord);
  const standard = studentRecord.standard || user?.standard || "";
  const division = studentRecord.division || user?.division || "";
  const targetClassKey = location.state?.studentClassKey || (standard && division ? classKey(standard, division) : "");
  const studentId = studentRecord.id || user?.id || user?.email || "";

  const summary = useMemo(() => {
    if (!targetClassKey || !standard) {
      return null;
    }
    return getFeeInstallmentProgress(targetClassKey, studentId, standard);
  }, [studentId, standard, targetClassKey]);

  const [parentEmail, setParentEmail] = useState(studentRecord.email || "");
  const [parentMobile, setParentMobile] = useState(studentRecord.phone || "");
  const [testAmount, setTestAmount] = useState(1);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (studentRecord.email) setParentEmail(studentRecord.email);
    if (studentRecord.phone) setParentMobile(studentRecord.phone);
  }, [studentRecord.email, studentRecord.phone]);

  if (!summary) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <Card className="border-slate-200 shadow-md">
            <CardContent className="py-14 text-center space-y-4">
              <p className="text-2xl font-bold text-slate-900">Fee details are not ready yet</p>
              <p className="text-slate-500">
                Please make sure the student profile has a class and division set.
              </p>
              <Button
                type="button"
                onClick={() => navigate("/fees")}
                className="rounded-full bg-[#0b1f7a] text-white hover:bg-[#09185f]"
              >
                Back to Fees
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  if (summary.isFullyPaid && !allowTestPayment) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto py-12 px-4">
          <Card className="border-slate-200 shadow-md">
            <CardContent className="py-14 text-center space-y-4">
              <p className="text-2xl font-bold text-slate-900">All installments are already paid</p>
              <p className="text-slate-500">
                There is no remaining fee due for this student right now.
              </p>
              <Button
                type="button"
                onClick={() => navigate("/fees")}
                className="rounded-full bg-[#0b1f7a] text-white hover:bg-[#09185f]"
              >
                Back to Fees
              </Button>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  const payableAmount = allowTestPayment ? Number(testAmount || 0) : Number(summary.nextAmount || 0);
  const dueDate = summary.nextDueDate || "Not set";
  const installmentLabel = allowTestPayment ? "Test Payment" : summary.nextInstallment?.label || "Next Installment";

  const handlePayNow = async () => {
    if (submitting) return;
    if (!parentEmail.trim() || !parentMobile.trim()) {
      toast.error("Please enter parent email and mobile number");
      return;
    }
    if (allowTestPayment && payableAmount <= 0) {
      toast.error("Please enter a test amount greater than zero");
      return;
    }

    setSubmitting(true);
    const paymentWindow = window.open("", "_blank");

    try {
      const result = await initiateFeePayment({
        studentId,
        studentName: studentRecord.studentName || studentRecord.name || user?.name || "Student",
        amount: payableAmount,
        parentEmail: parentEmail.trim(),
        parentMobile: parentMobile.trim(),
        standard,
        division,
        dueDate,
      });

      if (!result?.redirectUrl) {
        throw new Error("Payment gateway did not return a redirect URL");
      }

      if (paymentWindow) {
        paymentWindow.location.href = result.redirectUrl;
      } else {
        window.location.href = result.redirectUrl;
      }

      toast.success("Redirecting to the payment gateway");
    } catch (error) {
      if (paymentWindow) {
        paymentWindow.close();
      }
      toast.error(error?.message || "Unable to start payment");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-4 py-8 space-y-8">
        <div>
          <h1 className="text-3xl md:text-4xl font-extrabold text-slate-900">Checkout</h1>
          <p className="mt-2 text-slate-500">
            Review the fee summary and complete the payment in the secure bank tab.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="border-slate-200 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <div className="flex items-start justify-between gap-4">
                <div>
              <CardTitle className="text-2xl text-slate-900">
                    {classLabelFromKey(targetClassKey)}
                  </CardTitle>
                  <p className="mt-1 text-sm text-slate-500">
                    {studentRecord.studentName || studentRecord.name || "Student"}
                  </p>
                </div>
                <Badge className="bg-[#0b1f7a] text-white hover:bg-[#0b1f7a]">
                  {installmentLabel}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="p-6">
              <DetailRow
                label={allowTestPayment ? "Test Amount" : "Payable Amount"}
                value={`₹${payableAmount.toLocaleString("en-IN")}`}
              />
              <DetailRow label="Due Date" value={dueDate} />
              <DetailRow label="Paid in Advance" value={`₹${summary.paidAmount.toLocaleString("en-IN")}`} />
              <DetailRow label="Total Fees" value={`₹${Number(summary.plan?.totalAmount || 0).toLocaleString("en-IN")}`} />
            </CardContent>
          </Card>

          <Card className="border-slate-200 shadow-md overflow-hidden">
            <CardHeader className="bg-gradient-to-r from-slate-50 to-white border-b border-slate-100">
              <CardTitle className="text-2xl text-slate-900">Parent details</CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div>
                <label className="text-sm font-semibold text-slate-700">Parent Email</label>
                <Input
                  type="email"
                  value={parentEmail}
                  onChange={(e) => setParentEmail(e.target.value)}
                  placeholder="parent@example.com"
                  className="mt-1"
                />
              </div>

              <div>
                <label className="text-sm font-semibold text-slate-700">Parent Mobile</label>
                <Input
                  type="tel"
                  value={parentMobile}
                  onChange={(e) => setParentMobile(e.target.value)}
                  placeholder="10 digit mobile number"
                  className="mt-1"
                />
              </div>

              {allowTestPayment && (
                <div>
                  <label className="text-sm font-semibold text-slate-700">Test Amount</label>
                  <Input
                    type="number"
                    min="1"
                    step="1"
                    value={testAmount}
                    onChange={(e) => setTestAmount(e.target.value)}
                    placeholder="1"
                    className="mt-1"
                  />
                  <p className="mt-1 text-xs text-slate-500">
                    This account has no outstanding fees. Use this field to run a payment test.
                  </p>
                </div>
              )}

              <div className="rounded-2xl bg-slate-50 border border-slate-200 p-4 text-sm text-slate-600">
                The payment will open in a new tab and return to the success landing page when complete.
                {allowTestPayment
                  ? " This is a test payment path for the dummy account."
                  : ` You are paying only the current installment: ${installmentLabel}.`}
              </div>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate("/fees")}
                  className="rounded-full flex-1"
                >
                  Back
                </Button>
                <Button
                  type="button"
                  onClick={handlePayNow}
                  disabled={submitting}
                  className="rounded-full flex-1 bg-[#0b1f7a] text-white hover:bg-[#09185f]"
                >
                  {submitting ? "Starting Payment..." : "Pay Now"}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AppShell>
  );
}
